import { motion, useReducedMotion } from "framer-motion";
import { Play, Info } from "lucide-react";
import { scrollToId } from "@/lib/scroll";

const EASE = [0.22, 1, 0.36, 1];

const MaskedLine = ({ children, delay }) => {
  const reduce = useReducedMotion();
  return (
    <span className="block overflow-hidden pb-2 -mb-2">
      <motion.span
        className="block"
        initial={reduce ? false : { y: "112%" }}
        animate={{ y: 0 }}
        transition={{ duration: 0.95, delay, ease: EASE }}
      >
        {children}
      </motion.span>
    </span>
  );
};

export const Hero = () => {
  const reduce = useReducedMotion();

  return (
    <section id="meet" data-testid="hero-section" className="scroll-mt-24 overflow-hidden bg-[#F7F1E6] pt-24 sm:pt-28 lg:pt-30">
      <div className="mx-auto max-w-7xl px-4 pb-12 sm:px-6 lg:px-8 lg:pb-16">
        <div className="mx-auto max-w-3xl text-center">
          <motion.p
            data-testid="hero-eyebrow"
            initial={reduce ? false : { opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.05, ease: EASE }}
            className="mb-5 flex items-center justify-center gap-2.5 text-sm font-extrabold uppercase tracking-[0.24em] text-[#115B5B]"
          >
            <span className="h-2.5 w-2.5 rounded-full bg-[#F3802A]" aria-hidden="true" />
            Meet Peanut Valet
          </motion.p>

          <h1
            data-testid="hero-headline"
            className="text-[2.55rem] font-black leading-[1.05] tracking-tight text-[#074345] sm:text-5xl lg:text-[4rem]"
          >
            <MaskedLine delay={0.15}>
              A new <em className="italic text-[#F3802A]">twist</em>
            </MaskedLine>
            <MaskedLine delay={0.3}>on snack time.</MaskedLine>
          </h1>

          <motion.p
            data-testid="hero-subtext"
            initial={reduce ? false : { opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.5, ease: EASE }}
            className="mx-auto mt-5 max-w-2xl text-base leading-relaxed text-[#243C39] sm:text-lg"
          >
            Meet Peanut Valet, a full-size hand-crank peanut sheller in development,
            designed to collect peanuts and shells in separate drawers for a tidier
            snack-time experience.
          </motion.p>

          <motion.div
            initial={reduce ? false : { opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.65, ease: EASE }}
            className="mt-7 flex flex-wrap items-center justify-center gap-4"
          >
            <button
              type="button"
              data-testid="hero-cta-keep-me-updated"
              onClick={() => scrollToId("#signup")}
              className="rounded-full bg-[#F3802A] px-8 py-4 text-base font-extrabold text-[#043436] shadow-[0_10px_30px_-10px_rgba(243,128,42,0.55)] transition-[transform,filter] duration-200 hover:scale-[1.04] hover:brightness-95 active:scale-[0.97]"
            >
              Keep Me Updated
            </button>
            <button
              type="button"
              data-testid="hero-cta-watch-vision"
              onClick={() => scrollToId("#vision-video")}
              className="inline-flex items-center gap-2.5 rounded-full border-2 border-[#074345] px-7 py-[14px] text-base font-extrabold text-[#074345] transition-[transform,background-color,color] duration-200 hover:scale-[1.03] hover:bg-[#074345] hover:text-[#F7F1E6] active:scale-[0.97]"
            >
              <Play className="h-4 w-4 fill-current" aria-hidden="true" />
              Watch the Vision
            </button>
          </motion.div>

          <motion.div
            initial={reduce ? false : { opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.7, delay: 0.78, ease: EASE }}
            className="mt-5"
          >
            <button
              type="button"
              data-testid="hero-link-partner-inquiries"
              onClick={() => scrollToId("#partner")}
              className="text-sm font-bold text-[#074345] underline decoration-[#F3802A] decoration-2 underline-offset-4 transition-colors hover:text-[#115B5B]"
            >
              Manufacturing &amp; Licensing Inquiries
            </button>
          </motion.div>

          <motion.p
            data-testid="hero-caption"
            initial={reduce ? false : { opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.88 }}
            className="mt-4 flex items-center justify-center gap-2 text-sm text-[#506460]"
          >
            <Info className="h-4 w-4 shrink-0" aria-hidden="true" />
            Product in development. Rendered visuals.
          </motion.p>
        </div>

        <motion.div
          initial={reduce ? false : { opacity: 0, scale: 0.97, y: 24 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.4, ease: EASE }}
          className="mt-8 lg:mt-10"
        >
          <div className="overflow-hidden rounded-[2rem] shadow-[0_40px_80px_-40px_rgba(7,67,69,0.35)]">
            <img
              data-testid="hero-image"
              src="/assets/peanut-valet-founder-hero.webp"
              srcSet="/assets/peanut-valet-founder-hero-768.webp 768w, /assets/peanut-valet-founder-hero-1200.webp 1200w, /assets/peanut-valet-founder-hero.webp 1672w"
              sizes="(max-width: 1280px) 100vw, 1216px"
              alt="David Hirschfield, Founder & Inventor of Peanut Valet, beside the Peanut Valet peanut-shelling machine."
              width="1672"
              height="941"
              loading="eager"
              fetchPriority="high"
              className="h-auto w-full"
            />
          </div>
        </motion.div>
      </div>
    </section>
  );
};
